import React from 'react';
import { ProductBuyOption, ProductBuyOptionInfo, ProductConfiguration, ProductOption, VariantItem } from '@contracts';
import Drop1 from '@images/icon-drop.svg';
import Drop2 from '@images/icon-drop2.svg';
import Drop3 from '@images/icon-drop3.svg';

export function generateProductBuyOptions(productOptions: ProductOption[], configurations: ProductConfiguration[]): ProductBuyOption[] {
  if (!Array.isArray(configurations) || !Array.isArray(productOptions)) {
    return [];
  }

  const productIdHasVariant = (input: string, variant: string): boolean => new RegExp(`\\d+${variant}`).test(input);

  const getProductBuyOptionInfoArray = (optionInfo: string): ProductBuyOptionInfo[] => {
    try {
      const infoObject = JSON.parse(optionInfo);
      const { content } = infoObject;
      return content;
    } catch (e) {
      return [];
    }
  };

  const getProductOptionIcon = (sku: string) => {
    if (productIdHasVariant(sku, 'sp')) {
      return <Drop3 />;
    }
    if (productIdHasVariant(sku, 'cs') || productIdHasVariant(sku, 'ns') || productIdHasVariant(sku, 's')) {
      return <Drop2 />;
    }
    return <Drop1 />;
  };

  const productBuyOptions: ProductBuyOption[] = [];
  productOptions.forEach((productOption) => {
    const { oneOffCount, subscriptionCount, variants } = calculateCounts(
      productOption.oneOffSku,
      productOption.subscriptionSku,
      productOption.subscriptionPlan,
      configurations
    );

    const productBuyOption = {
      oneOffSku: productOption.oneOffSku,
      subscriptionSku: productOption.subscriptionSku,
      subscriptionPlan: productOption.subscriptionPlan,
      label: productOption.label,
      title: productOption.title,
      icon: getProductOptionIcon(productOption.oneOffSku ?? productOption.subscriptionSku),
      oneOffCount,
      subscriptionCount,
      includes: productOption.includes.split(','),
      variants,
      optionInfo: getProductBuyOptionInfoArray(productOption.optionInfo ?? '')
    };
    productBuyOptions.push(productBuyOption);
  });

  return productBuyOptions;
}

export function getFlattenedConfiguration(config: ProductConfiguration | undefined, configurations: ProductConfiguration[]): VariantItem[] {
  if (!config || !config.configuration) {
    return [];
  }

  return config.configuration.flatMap((item) => {
    const innerConfig = configurations.find((c) => c.variant === item.variant);

    if (innerConfig) {
      const newConfig = innerConfig.configuration || [];

      if (item.count && item.count > 1) {
        return newConfig.map((i) => ({ ...i, count: i.count ? i.count * item.count : item.count }));
      }

      return newConfig;
    }
    return [item];
  });
}

export function calculateCounts(oneOffSku: string, subscriptionSku: string, subscriptionPlan: string, configurations: ProductConfiguration[]) {
  const oneOffConfig = configurations.find((c) => c.variant === oneOffSku);
  const subscriptionConfig = configurations.find((c) => c.variant === subscriptionSku);

  const subscriptionVariants = getFlattenedConfiguration(subscriptionConfig, configurations);
  const oneOffVariants = getFlattenedConfiguration(oneOffConfig, configurations);

  const classesSet1 = new Set(['pnp', 'pr', 'pn', 'pl']);
  const classesSet2 = new Set(['cbdsuper', 'cbdregular', 'nakedsuper', 'nakedregular']);

  // Check if all items belong to the same class set
  const allOfSameClassSet = (items: VariantItem[]) => {
    // Determine which class sets the items belong to
    const belongToSet1 = items.every((item) => classesSet1.has(item.variant));
    const belongToSet2 = items.every((item) => classesSet2.has(item.variant));

    // Ensure all items belong to only one of the class sets, not both or neither
    return (belongToSet1 && !belongToSet2) || (!belongToSet1 && belongToSet2);
  };

  const sumCountIfSameClassSet = (items: VariantItem[]) => {
    if (allOfSameClassSet(items)) {
      return items.reduce((acc, item) => acc + item.count, 0);
    }
    return 0;
  };

  const subscriptionCount: number = subscriptionVariants ? sumCountIfSameClassSet(subscriptionVariants) : 0;
  const oneOffCount: number = oneOffVariants ? sumCountIfSameClassSet(oneOffVariants) : 0;

  const variants: { [key: string]: VariantItem[] } = {};

  if (subscriptionPlan && subscriptionVariants.length > 0) {
    variants[subscriptionPlan] = subscriptionVariants;
  }

  if (oneOffSku && oneOffVariants.length > 0) {
    variants['one-off'] = oneOffVariants;
  }

  return {
    subscriptionCount,
    oneOffCount,
    variants
  };
}
